import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  Chip,
  useTheme
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@services/authService';
import clientDashboardService from '@services/clientDashboardService';

interface Animal {
  id: string;
  name: string;
  species: string;
  breed?: string;
  profileimage?: string;
  photo_url?: string;
  photoUrl?: string;
  gender?: string;
  weight?: number;
  microchipId?: string;
  microchip_id?: string;
  birthDate?: string;
  color?: string;
  age?: number;
}

interface Clinic {
  id: string;
  name: string;
  address: any;
  phone?: string;
}

interface Veterinarian {
  id: string;
  firstName: string;
  lastName: string;
  specialization?: string;
}

interface TimeSlot {
  id: string;
  start: string;
  end: string;
  available: boolean;
}

interface AppointmentWizardProps {
  preselectedClinicId?: string;
  preselectedAnimalId?: string;
}

const steps = ['Selecteaza animalul', 'Selecteaza veterinarul', 'Data si ora', 'Detalii'];

const AppointmentWizard: React.FC<AppointmentWizardProps> = ({
  preselectedClinicId,
  preselectedAnimalId
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const params = useParams();
  
  // Extract from URL params if provided
  const clinicIdFromUrl = params.clinicId || preselectedClinicId;
  const animalIdFromUrl = params.animalId || preselectedAnimalId;

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form data
  const [selectedAnimalId, setSelectedAnimalId] = useState<string>(animalIdFromUrl || '');
  const [selectedClinicId, setSelectedClinicId] = useState<string>(clinicIdFromUrl || '');
  const [selectedVetId, setSelectedVetId] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedSlotId, setSelectedSlotId] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [isEmergency, setIsEmergency] = useState<boolean>(false);

  // Data lists
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [veterinarians, setVeterinarians] = useState<Veterinarian[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);

  // Load animals
  useEffect(() => {
    loadAnimals();
  }, []);

  // Load clinics if not preselected
  useEffect(() => {
    if (!clinicIdFromUrl) {
      loadClinics();
    }
  }, [clinicIdFromUrl]);

  // Load veterinarians when clinic is selected (auto-load if preselected)
  useEffect(() => {
    if (selectedClinicId) {
      loadVeterinarians(selectedClinicId);
    }
  }, [selectedClinicId]);

  // Auto-load veterinarians if clinic is preselected from URL
  useEffect(() => {
    if (clinicIdFromUrl) {
      setSelectedClinicId(clinicIdFromUrl);
      loadVeterinarians(clinicIdFromUrl);
    }
  }, [clinicIdFromUrl]);

  const loadAnimals = async () => {
    try {
      const patients = await clientDashboardService.getPatients();
      setAnimals(
        patients.map((patient) => ({
          id: patient.id,
          name: patient.name,
          species: patient.species,
          breed: patient.breed,
          photo_url: patient.photo_url || undefined,
          gender: patient.sex,
          weight: patient.weight || undefined,
          microchipId: patient.microchip || undefined,
          birthDate: patient.dateOfBirth || undefined,
        }))
      );
    } catch {
      setError('Eroare la incarcarea animalelor');
    }
  };

  const loadClinics = async () => {
    try {
      const response = await api.get('/clinics/partner');
      setClinics(response.data.data || []);
    } catch {
      setError('Eroare la incarcarea clinicilor');
    }
  };

  const loadVeterinarians = async (clinicId: string) => {
    try {
      const response = await api.get(`/clinics/${clinicId}/vets`);
      setVeterinarians(response.data.data || []);
    } catch {
      setError('Eroare la incarcarea veterinarilor');
    }
  };

  const loadTimeSlots = useCallback(async (vetId: string, date: string) => {
    try {
      const start = new Date(date);
      start.setHours(0, 0, 0, 0);
      const end = new Date(date);
      end.setHours(23, 59, 59, 999);

      const slots = await clientDashboardService.getAvailability({
        clinic_id: selectedClinicId,
        doctor_id: vetId,
        from: start.toISOString(),
        to: end.toISOString(),
      });
      setTimeSlots(
        slots.map((slot, index) => ({
          id: `slot-${index}`,
          start: slot.start,
          end: slot.end,
          available: slot.available,
        }))
      );
    } catch {
      setError('Eroare la incarcarea sloturilor disponibile');
    }
  }, [selectedClinicId]);

  // Load time slots when vet and date are selected
  useEffect(() => {
    if (selectedVetId && selectedDate) {
      loadTimeSlots(selectedVetId, selectedDate);
    }
  }, [selectedVetId, selectedDate, loadTimeSlots]);

  const handleNext = () => {
    // Validare pentru fiecare step (fara pasul de clinica)
    if (activeStep === 0 && !selectedAnimalId) {
      setError('Te rugam sa selectezi un animal');
      return;
    }
    if (activeStep === 1 && !selectedVetId) {
      setError('Te rugam sa selectezi un veterinar');
      return;
    }
    if (activeStep === 2 && (!selectedDate || !selectedSlotId)) {
      setError('Te rugam sa selectezi data si ora');
      return;
    }
    if (activeStep === 3 && !reason.trim()) {
      setError('Te rugam sa introduci motivul programarii');
      return;
    }

    setError(null);
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Gaseste slotul selectat pentru a obtine scheduledAt
      const selectedSlot = timeSlots.find(s => s.id === selectedSlotId);
      if (!selectedSlot) {
        setError('Te rugam sa selectezi un slot disponibil');
        setLoading(false);
        return;
      }

      const appointmentData = {
        clinic_id: selectedClinicId,
        patient_id: selectedAnimalId,
        doctor_id: selectedVetId,
        start_ts: selectedSlot.start,
        reason: reason.trim(),
      };

      await clientDashboardService.createAppointment(appointmentData);

      // Success - navigate to appointments
      navigate('/appointments');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Eroare la crearea programarii');
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        // Selecteaza animalul
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Pentru care animal programezi vizita?
            </Typography>
            {animals.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                Nu ai niciun animal inregistrat. <Button onClick={() => navigate('/animals')}>Adauga un animal</Button>
              </Alert>
            ) : (
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, 
                gap: 3, 
                mt: 2,
                maxWidth: '100%'
              }}>
                {animals.map((animal) => (
                  <Card
                    key={animal.id}
                    onClick={() => setSelectedAnimalId(animal.id)}
                    sx={{
                      cursor: animalIdFromUrl ? 'not-allowed' : 'pointer',
                      border: selectedAnimalId === animal.id ? 2 : 1,
                      borderColor: selectedAnimalId === animal.id ? '#2D7FF9' : 'divider',
                      opacity: animalIdFromUrl && animalIdFromUrl !== animal.id ? 0.5 : 1,
                      pointerEvents: animalIdFromUrl && animalIdFromUrl !== animal.id ? 'none' : 'auto',
                      transition: 'all 0.2s ease-in-out',
                      borderRadius: 3,
                      overflow: 'hidden',
                      position: 'relative',
                      '&:hover': {
                        borderColor: animalIdFromUrl ? 'divider' : '#2D7FF9',
                        boxShadow: animalIdFromUrl ? 'none' : '0 4px 12px rgba(45, 127, 249, 0.15)',
                        transform: animalIdFromUrl ? 'none' : 'translateY(-2px)'
                      }
                    }}
                  >
                    {/* Poza animalului */}
                    <Box sx={{ position: 'relative', height: 200, overflow: 'hidden' }}>
                      {animal.profileimage || animal.photo_url || animal.photoUrl ? (
                        <Box
                          component="img"
                          src={animal.profileimage || animal.photo_url || animal.photoUrl}
                          alt={animal.name}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <Box sx={{
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '4rem'
                        }}>
                          {animal.species === 'dog' ? '🐶' : 
                           animal.species === 'cat' ? '🐱' : 
                           animal.species === 'bird' ? '🐦' : 
                           animal.species === 'rabbit' ? '🐰' : 
                           animal.species === 'horse' ? '🐴' : '🐾'}
                        </Box>
                      )}
                      
                      {/* Chip pentru specie */}
                      <Chip
                        label={animal.species === 'dog' ? 'Caine' : 
                               animal.species === 'cat' ? 'Pisica' : 
                               animal.species === 'bird' ? 'Pasare' : 
                               animal.species === 'rabbit' ? 'Iepure' : 
                               animal.species === 'horse' ? 'Cal' : 'Animal'}
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          bgcolor: animal.species === 'dog' ? 'success.main' : 
                                   animal.species === 'cat' ? 'secondary.main' : 'primary.main',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      />
                    </Box>

                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        {animal.name}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {animal.breed || animal.species} • {animal.gender === 'male' ? 'Mascul' : 'Femela'}
                      </Typography>

                      {animal.weight && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Greutate: {animal.weight} kg
                        </Typography>
                      )}

                      {(animal.microchipId || animal.microchip_id) && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          Microchip: {animal.microchipId || animal.microchip_id}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        );

      case 1:
        // Selecteaza veterinarul
        return (
          <Box>
            {clinicIdFromUrl && (
              <Alert severity="info" sx={{ mb: 3 }}>
                🏥 Clinica este deja selectata pentru aceasta programare.
              </Alert>
            )}
            <Typography variant="h6" gutterBottom>
              Alege veterinarul
            </Typography>
            {veterinarians.length === 0 ? (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Nu sunt veterinari disponibili pentru aceasta clinica.
              </Alert>
            ) : (
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, 
                gap: 2, 
                mt: 2 
              }}>
                {veterinarians.map((vet) => (
                  <Card
                    key={vet.id}
                    onClick={() => setSelectedVetId(vet.id)}
                    sx={{
                      cursor: 'pointer',
                      border: selectedVetId === vet.id ? 2 : 1,
                      borderColor: selectedVetId === vet.id ? '#2D7FF9' : 'divider',
                      transition: 'all 0.2s ease-in-out',
                      position: 'relative',
                      '&:hover': {
                        borderColor: '#2D7FF9',
                        boxShadow: '0 4px 12px rgba(45, 127, 249, 0.15)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    {selectedVetId === vet.id && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1,
                          bgcolor: '#2D7FF9',
                          borderRadius: '50%',
                          width: 24,
                          height: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'white', fontSize: '0.8rem' }}>
                          ✔️
                        </Typography>
                      </Box>
                    )}
                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                      <Avatar 
                        sx={{ 
                          width: 80, 
                          height: 80, 
                          mx: 'auto', 
                          mb: 2,
                          bgcolor: 'primary.main',
                          fontSize: '1.8rem'
                        }}
                      >
                        👩‍⚕️
                      </Avatar>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        Dr. {vet.firstName} {vet.lastName}
                      </Typography>
                      <Typography variant="body2" color="primary.main" fontWeight={500} sx={{ mb: 1 }}>
                        {vet.specialization || 'Medicina generala'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Expert in {vet.specialization?.toLowerCase() || 'medicina veterinara'}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        );

      case 2:
        // Selecteaza data si ora
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Cand doresti programarea?
            </Typography>
            <TextField
              fullWidth
              type="date"
              label="Data"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 2 }}
              inputProps={{
                min: new Date().toISOString().split('T')[0]
              }}
            />
            {selectedDate && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Sloturi disponibile:
                </Typography>
                {timeSlots.length === 0 ? (
                  <Alert severity="info">Nu sunt sloturi disponibile pentru aceasta data</Alert>
                ) : (
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(5, 1fr)' }, 
                    gap: 1.5, 
                    mt: 2 
                  }}>
                    {timeSlots.map((slot) => {
                      const isSelected = selectedSlotId === slot.id;
                      const isAvailable = slot.available;
                      const timeStr = new Date(slot.start).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
                      
                      return (
                        <Box
                          key={slot.id}
                          onClick={() => isAvailable && setSelectedSlotId(slot.id)}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            textAlign: 'center',
                            cursor: isAvailable ? 'pointer' : 'not-allowed',
                            transition: 'all 0.2s ease-in-out',
                            border: 2,
                            borderColor: isSelected ? '#2D7FF9' : isAvailable ? '#e0e0e0' : '#f5f5f5',
                            bgcolor: isSelected ? '#2D7FF9' : isAvailable ? 'background.paper' : '#f5f5f5',
                            color: isSelected ? 'white' : isAvailable ? 'text.primary' : 'text.disabled',
                            '&:hover': isAvailable ? {
                              borderColor: isSelected ? '#2D7FF9' : '#2D7FF9',
                              bgcolor: isSelected ? '#2D7FF9' : 'rgba(45, 127, 249, 0.05)',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 2px 8px rgba(45, 127, 249, 0.2)'
                            } : {}
                          }}
                        >
                          <Typography 
                            variant="body2" 
                            fontWeight={isSelected ? 600 : 500}
                            sx={{ fontSize: '0.875rem' }}
                          >
                            {timeStr}
                          </Typography>
                          {!isAvailable && (
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                              Ocupat
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        );

      case 3:
        // Detalii programare
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Detalii despre programare
            </Typography>
            <TextField
              fullWidth
              label="Motivul programarii *"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              rows={2}
              sx={{ mt: 2 }}
              placeholder="Ex: Control de rutina, Vaccinare, Consultatie..."
            />
            <TextField
              fullWidth
              label="Note suplimentare"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={3}
              sx={{ mt: 2 }}
              placeholder="Simptome, comportament, alte observatii..."
            />
            <FormControlLabel
              control={
                <Switch
                  checked={isEmergency}
                  onChange={(e) => setIsEmergency(e.target.checked)}
                />
              }
              label="Programare de urgenta"
              sx={{ mt: 2 }}
            />
            {isEmergency && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Programarile de urgenta vor fi tratate cu prioritate si vor fi confirmate mai rapid.
              </Alert>
            )}
          </Box>
        );

      case 4:
        // Sumar final
        const selectedAnimal = animals.find(a => a.id === selectedAnimalId);
        const selectedClinic = clinics.find(c => c.id === selectedClinicId);
        const selectedVet = veterinarians.find(v => v.id === selectedVetId);
        const selectedSlot = timeSlots.find(s => s.id === selectedSlotId);

        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Confirma programarea
            </Typography>
            <Card sx={{ mt: 2, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Animal:</Typography>
                    <Typography variant="body1" fontWeight={600}>{selectedAnimal?.name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Clinica:</Typography>
                    <Typography variant="body1" fontWeight={600}>{selectedClinic?.name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Veterinar:</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      Dr. {selectedVet?.firstName} {selectedVet?.lastName}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Data si ora:</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {selectedDate && new Date(selectedDate).toLocaleDateString('ro-RO', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                      {selectedSlot && ` la ${new Date(selectedSlot.start).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}`}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Motiv:</Typography>
                    <Typography variant="body1">{reason}</Typography>
                  </Box>
                  {notes && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Note:</Typography>
                      <Typography variant="body2">{notes}</Typography>
                    </Box>
                  )}
                  {isEmergency && (
                    <Chip label="🚨 Urgenta" color="error" size="small" sx={{ alignSelf: 'flex-start' }} />
                  )}
                </Box>
              </CardContent>
            </Card>
            <Alert severity="info" sx={{ mt: 2 }}>
              Programarea ta va fi trimisa catre clinica si va fi in asteptarea confirmarii.
              Vei primi o notificare cand clinica va confirma sau respinge programarea.
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', py: 4 }}>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        Programeaza o vizita
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Completeaza pasii de mai jos pentru a programa o consultatie
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mt: 3 }}>
        {renderStepContent()}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          variant="outlined"
        >
          Inapoi
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/appointments')}
          >
            Anuleaza
          </Button>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Se programeaza...' : 'Programeaza'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
            >
              Continua
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AppointmentWizard;

